/**
 * Resolves the fax form id for questionnaire submit from the persisted
 * submission row instead of in-memory Redux.
 *
 * `singleMedicalPaFormConfig` is only populated when the user walks in from
 * form selection, so a page refresh on Procedure Questionnaire would lose it
 * and silently skip `/fax/submit`. Both sources here survive a refresh:
 * `latest_form_data.selected_form_id_for_submission` is written on medical PA
 * form save (before any fax artefact exists), and `submission_output.form_id`
 * is written after a fax send, covering retries.
 *
 * @module procedureQuestionaire/utils/faxFormIdResolver
 */
import { Submission } from "../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";

/**
 * Normalizes a Postgres JSONB value into a plain object.
 *
 * @param value - Raw column value, which the API may return as a JSON string
 * @returns Parsed object, or an empty object when the value is unusable
 */
const asJsonObject = (value: unknown): Record<string, unknown> => {
  let parsed = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {};
  }
  return parsed as Record<string, unknown>;
};

/**
 * Reads a non-empty string field off a normalized JSONB object.
 *
 * @param source - Normalized JSONB object
 * @param key - Field to read
 * @returns The trimmed-non-empty value, or undefined when missing/blank
 */
const readNonEmptyString = (
  source: Record<string, unknown>,
  key: string,
): string | undefined => {
  const value = source[key];
  return typeof value === "string" && value.trim() ? value : undefined;
};

/**
 * Resolves the fax form id from the submission row, newest-persisted last.
 *
 * @param submission - `submission` row from the single-order accumulator
 * @returns The selected form id, or an empty string when nothing is persisted
 */
export const resolveFaxFormId = (
  submission: Submission | null | undefined,
): string => {
  const latestFormData = asJsonObject(submission?.latest_form_data);
  const selectedFormId = readNonEmptyString(
    latestFormData,
    "selected_form_id_for_submission",
  );
  if (selectedFormId) {
    return selectedFormId;
  }

  const submissionOutput = asJsonObject(submission?.submission_output);
  return readNonEmptyString(submissionOutput, "form_id") ?? "";
};
